/**
 * Session hydration — the first slice moved off browser-composed queries.
 *
 * The app used to build three queries here and post them to __table_proxy:
 * the profile row, the caller's membership, and then — only once the
 * membership had come back with a company id — that company's subscription
 * tier. The third could not start until the second finished, so every app
 * load paid two sequential network waves before the first screen painted.
 *
 * This is now one named endpoint. The frontend no longer decides which tables
 * to read, which columns to select, or how to order them; it asks for the
 * caller's session context and the server decides what that means.
 *
 * Contract mirror: labourcore-api/src/modules/session/session.types.ts.
 * The repos don't share a package, so change one and change the other.
 */

import { apiGet } from "./client";
import type { Profile } from "./types";

/** The five spine positions. Mirrors the `tiers` table's primary key. */
export type TierCode = "owner" | "consultant" | "tier1" | "mep" | "operative";

export interface SessionContext {
  /** null when the account has no profiles row yet. */
  profile: Profile | null;
  /**
   * The caller's most recently created non-deleted membership.
   * null means no tenant — the caller then runs its self-heal path.
   *
   * "Most recent" is load-bearing, not incidental: __table_proxy defaulted to
   * ORDER BY created_at DESC when no order was given, and some accounts hold
   * several memberships. Picking a different one silently switches the user's
   * tenant. The server replicates that ordering explicitly.
   */
  company_id: string | null;
  /** Fails closed — anything that isn't exactly 'pro' resolves to 'free'. */
  subscription_tier: "free" | "pro";
  /**
   * Every company the caller may act as, oldest membership first.
   *
   * Note this can DISAGREE with `company_id` above, which is the most recent
   * membership — and that disagreement is exactly the bug GH #167 described.
   * The acting-context store chooses from this list, never from `company_id`.
   */
  memberships: SessionMembership[];
  /**
   * Stable opaque mail alias, computed by the server.
   *
   * The browser used to build its own with a different pepper, a different
   * separator and a single hash doubled — so the two values were never the
   * same for the same user. Its other ingredient was `profiles.role`, which
   * migration 029 dropped. One definition now, and it lives where the data is.
   */
  mail_id: string | null;

  /** The catalogue — every active tier, so the Spine modal can draw locked bars. */
  tiers: Array<{
    code: TierCode; name: string; strapline: string | null; rank: number;
    subject: "company" | "person"; fee_label: string | null;
    package_label: string | null; is_free: boolean; home_route: string | null;
  }>;

  /** The grants — what THIS caller may act as. company_id is null for a person-tier. */
  actable_tiers: Array<{
    tier_code: TierCode; company_id: string | null;
    is_primary: boolean; subject: "company" | "person"; rank: number;
    /** Demo mode only: an entitlement the company does not really hold. */
    via_demo?: boolean;
  }>;

  /**
   * Is this DEPLOYMENT running with demo mode on?
   *
   * Server-decided and read-only. The browser is told; it can never ask. A
   * tampered client that forces this true gets a control whose every request
   * the API then refuses — the flag lives on the server, not here.
   */
  demo_mode?: boolean;

  /** Capabilities in the ACTING company. Presentation only — the guard decides. */
  capabilities: Array<{ code: string; scope: "any" | "own" }>;
}

export interface SessionMembership {
  company_id: string;
  name: string | null;
  kind: "coordinator" | "partner" | "hybrid";
  role: string;
}

/**
 * Throws ApiError('SESSION_EXPIRED') via authedFetch when the refresh fails,
 * which is what lets the caller tell a dead session apart from a slow one.
 */
export function fetchSessionContext(): Promise<SessionContext> {
  return apiGet<SessionContext>("/api/session/context");
}
